import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from "@showitinc/showit-wc";
import styles from "./Modal.module.css";

/**
 * A modal dialog component that can be rendered anywhere in the DOM using a portal.
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} [props.title="title"] - The title text shown in the modal header
 * @param {boolean} props.isOpen - Whether the modal is currently visible
 * @param {Function} props.onClose - Callback function when modal is closed
 * @param {boolean} [props.hasClose=true] - Whether to show the close button
 * @param {React.ReactNode} [props.children=<div>children</div>] - Content to render in the modal body
 * @param {React.ReactNode} [props.footer] - Custom footer content
 * @param {?string} [props.size="medium"] - Preset size class (small|medium|large|fullscreen); pass null to opt out and size the dialog via className
 * @param {boolean} [props.closeOnOverlayClick=true] - Whether clicking overlay closes modal
 * @param {string} [props.leftButtonTitle] - Text for the left footer button
 * @param {Function} [props.leftButtonAction] - Click handler for left footer button
 * @param {string} [props.secondaryButtonTitle] - Text for the secondary footer button
 * @param {Function} [props.secondaryButtonAction] - Click handler for secondary footer button
 * @param {string} [props.primaryButtonTitle] - Text for the primary footer button
 * @param {Function} [props.primaryButtonAction] - Click handler for primary footer button
 * @param {boolean} [props.leftButtonDisabled=false] - Whether left button is disabled
 * @param {boolean} [props.secondaryButtonDisabled=false] - Whether secondary button is disabled
 * @param {boolean} [props.primaryButtonDisabled=false] - Whether primary button is disabled
 * @param {string} [props.primaryButtonVariant="feature"] - Variant style for primary button
 * @param {string} [props.secondaryButtonVariant="secondary"] - Variant style for secondary button
 * @param {string} [props.leftButtonVariant="outline"] - Variant style for left button
 * @param {string} [props.className] - Extra class applied to the dialog (e.g. custom sizing)
 * @returns {React.ReactPortal|null} The modal portal or null if closed
 */

const Modal = ({
  title = "title",
  isOpen,
  onClose,
  hasClose = true,
  children = <div>children</div>,
  footer,
  size = "medium",
  className = "",
  closeOnOverlayClick = true,
  leftButtonTitle,
  leftButtonAction,
  secondaryButtonTitle,
  secondaryButtonAction,
  primaryButtonTitle,
  primaryButtonAction,
  leftButtonDisabled = false,
  secondaryButtonDisabled = false,
  primaryButtonDisabled = false,
  primaryButtonVariant = "feature",
  secondaryButtonVariant = "minimal",
  leftButtonVariant = "outline",
}) => {
  const dialogRef = useRef(null);

  // TODO: Revist this to not use a useEffect for focusing the dialog
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  const modalSizeClass = size ? styles[size] || styles.medium : "";

  return createPortal(
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <dialog
        ref={dialogRef}
        className={`${styles.modal} ${modalSizeClass}${className ? ` ${className}` : ""}`}
        aria-modal="true"
        aria-labelledby="modal-title"
        onKeyDown={handleKeyDown}
      >
        <div className={styles.header}>
          <h2 id="modal-title" className={styles.title}>
            {title}
          </h2>
          {hasClose && <Button onClick={onClose} variant="icon" iconPrefix="close" className={styles["close-btn"]} />}
        </div>

        {children}

        {footer ? (
          <div className={`${styles.footer} ${styles.footerCustom}`}>{footer}</div>
        ) : leftButtonTitle || secondaryButtonTitle || primaryButtonTitle ? (
          <div className={`${styles.footer} ${styles.footerDefault}`}>
            <div className="footer-left">
              {leftButtonTitle && (
                <Button
                  variant={leftButtonVariant}
                  onSIClick={leftButtonAction}
                  disabled={leftButtonDisabled}
                >
                  {leftButtonTitle}
                </Button>
              )}
            </div>
            <div className={styles.footerRight}>
              {secondaryButtonTitle && (
                <Button
                  variant={secondaryButtonVariant}
                  onSIClick={secondaryButtonAction}
                  disabled={secondaryButtonDisabled}
                >
                  {secondaryButtonTitle}
                </Button>
              )}
              {primaryButtonTitle && (
                <Button
                  variant={primaryButtonVariant}
                  onSIClick={primaryButtonAction}
                  disabled={primaryButtonDisabled}
                >
                  {primaryButtonTitle}
                </Button>
              )}
            </div>
          </div>
        ) : null}
      </dialog>
    </div>,
    document.body
  );
};

export default Modal;
